import { DirectDebitMemberProfile, AdditionalDirectDebitItem } from '../../model/payments/DirectDebitMemberProfile';
import { getCollectableItems } from '../../utils/recurringItemValidity';

/**
 * What a member's direct debit collects on a given date.
 *
 * This is the amount that ends up in the bank file, so it lives here rather than
 * inside the grid component: it is money logic, and it needs tests of its own.
 */

/** The additional items a run on `collectionDate` charges for this member. */
export const getCollectableAdditionalItems = (
    additionalItems: AdditionalDirectDebitItem[] | null | undefined,
    collectionDate: Date
): AdditionalDirectDebitItem[] => getCollectableItems(additionalItems, collectionDate);

/**
 * The membership subscription plus every additional item still running on
 * `collectionDate`. Only Monthly categories contribute a subscription — an
 * annual member with a monthly locker fee collects the locker fee alone.
 */
export const calculateTotalPaymentAmount = (
    member: DirectDebitMemberProfile,
    collectionDate: Date
): number => {
    const isMonthly = member.profile.membershipCategory.paymentFrequency === 'Monthly';
    const baseAmount = isMonthly ? member.profile.membershipCategory.amount : 0;
    const additionalAmount = getCollectableAdditionalItems(member.additionalDirectDebitItems, collectionDate)
        .reduce((sum, item) => sum + item.amount, 0);
    return baseAmount + additionalAmount;
};

/**
 * What the run will actually collect for this member: the operator's override when
 * they have set one on this row, otherwise the calculated total. One helper so the
 * grid cell, the selection summary, and the request payload can never disagree.
 */
export const effectivePaymentAmount = (
    member: DirectDebitMemberProfile,
    collectionDate: Date,
    overriddenAmounts: Map<string, number>
): number => {
    const override = overriddenAmounts.get(member.profile.membershipId);
    return override !== undefined ? override : calculateTotalPaymentAmount(member, collectionDate);
};
